import { API } from '@/config/api';
import { apiClient } from '@/shared/api/apiClient';

/** Who the server thinks the caller is. */
/**
 * The signed-in account, as `GET /api/v1/account/me` publishes it.
 *
 * <p><b>Hand-written, and therefore owed a check.</b> `components['schemas']['CurrentUser']`
 * in `schema.d.ts` is the generated shape, and this interface shadows it — so the compiler
 * has no contract to check and `tsc` passes whatever the server actually sends. That is the
 * hole CLAUDE.md rule 6 describes, and the one that cost `/team` a two-day outage when a
 * field was removed server-side and the hand-written type kept it.
 *
 * <p><b>This paragraph said the generated shape had no `products` until 14 October 2026, and
 * it was wrong</b> — regenerating against a running backend showed `products`, `company` and
 * `CurrentUserCompany` all present and correct, and the only real difference was the field
 * this commit adds. The claim had been true when it was written and nobody re-read it, which
 * is the same staleness it exists to warn about, one level up. Do not trust it again without
 * running `npm run api:types`.
 *
 * <p><b>The generated type still cannot replace this one, and the reason is not staleness.</b>
 * Springdoc declares no `required` list for this schema, so every generated field is
 * optional — `id?: string`, `email?: string`, and so on. Adopting it would turn every read
 * in the application into a null check for a value the server always sends. Deleting this
 * interface is possible only once the backend marks those fields required in the document.
 */
export interface CurrentUser {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  /** `SIGN_UP_PENDING` until an activation link is spent or an admin activates. */
  status: string;
  /** False when an administrator opened the account rather than the mailbox being proved. */
  emailVerified: boolean;

  /**
   * Whether this account has a password it can sign in with.
   *
   * <p>False for somebody who registered through Google and has never set one. The server
   * reads `user_account.password` and publishes only whether it is there, never the hash.
   *
   * <p>It exists so a screen can offer **Set password** or **Change password** without
   * guessing, and **nothing reads it yet** — neither screen is built. The three fields that
   * look like they would answer the question do not: a Google account reaches
   * `SIGN_UP_COMPLETED` and proves its mailbox without ever choosing a password, and
   * somebody can hold a password and a provider link at once.
   */
  hasPassword: boolean;
  roles: string[];

  /**
   * What this person may open, keyed by `product_key`, with what they may do inside it.
   *
   * <p>`{ email_warmup: { privileges: ['create', 'edit', 'run', 'view'] } }`. A key is
   * present when they hold at least one privilege on that product and it is still on sale —
   * the same question the server's own `@RequiresProduct` answers on that product's
   * endpoints, so this cannot promise a screen the API will refuse.
   *
   * <p><b>The key set is the membership. There is no `productKeys` any more.</b> The server
   * published one beside a `productPrivileges` map until 5 October 2026, and it was that
   * map's key set — one fact twice, which nothing could make disagree and nobody could tell
   * was derived. `Object.keys(user.products)` where a list is needed, locally, at the point
   * of use; do not keep a second copy in state. Backend `product-privileges.md` PP-31.
   *
   * <p><b>Grouped because a grant is (user, privilege, product).</b> `create` on Email
   * Warmup says nothing about `create` on the SEO AI Agent. Never flatten the privileges
   * across products.
   *
   * <p><b>This person's grants, not their company's vocabulary.</b>
   * `GET /api/v1/product-privileges` publishes what a company MAY assign, which is what the
   * Team Add form is built from and what `useProductPrivileges` reads. Different question,
   * confusingly similar name.
   *
   * <p>`{}` is a real answer — an invited team member whose administrator ticked nothing.
   * Never absent, never null, and the same shape for every role; the platform operator holds
   * no tenant products and gets `{}`. Somebody with no session at all is `user === null`,
   * which is a different thing and the reason this is not optional.
   *
   * <p><b>Nothing reads the privileges yet, and they are not permission.</b> The server has
   * no privilege-level check on any endpoint: `@RequiresProduct` gates the product. Hiding a
   * control somebody cannot use is a courtesy to them, not a boundary — backend
   * `product-privileges.md` PP-31h.
   */
  products: Record<string, { privileges: string[] }>;

  company: {
    id: string;
    name: string;
  };
}

/**
 * Resolves the session to an identity.
 *
 * <p>The access token is opaque — a reference, not a JWT — so nothing about the
 * person can be read out of it in the browser. This endpoint is the only way to
 * know who is signed in, which is also why it is the honest check that a token
 * still works.
 */
export async function fetchCurrentUser(): Promise<CurrentUser> {
  return apiClient.get<CurrentUser>(API.currentUser);
}

/**
 * Ends the session on the server.
 *
 * <h2>Why forgetting the token locally is not signing out</h2>
 *
 * The access token is a reference the server holds a row for, so clearing storage
 * only makes this browser stop presenting it — the token keeps working until it
 * expires, for anybody who has a copy. On a shared machine that is the whole
 * problem: the person who "signed out" left a working credential behind.
 *
 * <p>Failures here are swallowed by the caller on purpose. If the network is down
 * the local session must still end, and refusing to sign somebody out because the
 * server could not be reached is the worst of both.
 */
export async function revokeSession(): Promise<void> {
  await apiClient.post<undefined>(API.logout);
}
